import {
  Zap,
  Smartphone,
  Link2,
  Users,
  Code,
  Database,
  Cloud,
  Shield,
  BarChart3,
  Stethoscope,
  Store,
  CalendarCheck,
  FileText,
  PillBottle,
  Activity,
  Package,
  ShoppingCart,
  TrendingUp,
  Receipt,
  Boxes,
  CreditCard,
} from 'lucide-react';

// Navigation item IDs and hrefs (labels come from translations).
// `type: 'route'` → react-router Link; `type: 'anchor'` → scroll to home section.
export const NAVIGATION_ITEMS = [
  { id: 'home', href: '#home', type: 'anchor' },
  { id: 'services', href: '#services', type: 'anchor' },
  { id: 'success-stories', href: '#success-stories', type: 'anchor' },
  { id: 'products', href: '/productos', type: 'route' },
  { id: 'contact', href: '#contact', type: 'anchor' },
];

// Services config (texts come from translations)
export const SERVICES_CONFIG = [
  { id: 'automatizaciones', icon: Zap },
  { id: 'mobile-apps', icon: Smartphone },
  { id: 'integraciones', icon: Link2 },
  { id: 'outsourcing', icon: Users },
  { id: 'desarrollo-web', icon: Code },
  { id: 'bases-de-datos', icon: Database },
  { id: 'cloud', icon: Cloud },
  { id: 'seguridad', icon: Shield },
  { id: 'analitica', icon: BarChart3 },
];

// Stats config (labels come from translations)
export const STATS_CONFIG = [
  { id: 'years', value: 10, suffix: '+' },
  { id: 'clients', value: 50, suffix: '+' },
  { id: 'projects', value: 100, suffix: '+' },
  { id: 'experts', value: 15, suffix: '+' },
];

// Success Stories config (texts come from translations)
export const SUCCESS_STORIES_CONFIG = [
  {
    id: 'story-1',
    image: '/images/cases/ecommerce.webp',
    imageMobile: '/images/cases/ecommerce-portrait-maxq.webp',
    link: '#',
  },
  {
    id: 'story-2',
    image: '/images/cases/logistica.webp',
    imageMobile: '/images/cases/logistica-portrait-maxq.webp',
    link: '#',
  },
  {
    id: 'story-3',
    image: '/images/cases/hoteleria.webp',
    imageMobile: '/images/cases/hoteleria-portrait-maxq.webp',
    link: '#',
  },
  {
    id: 'story-4',
    image: '/images/cases/analytics.webp',
    imageMobile: '/images/cases/analytics-portrait-maxq.webp',
    link: '#',
  },
  {
    id: 'story-5',
    image: '/images/cases/fintech.webp',
    imageMobile: '/images/cases/fintech-portrait-maxq.webp',
    link: '#',
  },
  {
    id: 'story-6',
    image: '/images/cases/educacion.webp',
    imageMobile: '/images/cases/educacion-portrait-maxq.webp',
    link: '#',
  },
];

// Products config (texts come from translations)
export const PRODUCTS_CONFIG = [
  {
    id: 'medicare',
    slug: 'medicare',
    route: '/productos/medicare',
    icon: Stethoscope,
    categoryIcon: Stethoscope,
    accent: 'medicare',
    features: [
      { id: 'appointments', icon: CalendarCheck },
      { id: 'records', icon: FileText },
      { id: 'prescriptions', icon: PillBottle },
      { id: 'monitoring', icon: Activity },
    ],
  },
  {
    id: 'tienda360',
    slug: 'tienda360',
    route: '/productos/tienda360',
    // Sitio externo del producto: los CTAs de la card redirigen acá (pestaña nueva)
    externalUrl: 'https://tienda360.io/',
    icon: Store,
    categoryIcon: Store,
    accent: 'tienda360',
    features: [
      { id: 'inventory', icon: Boxes },
      { id: 'pos', icon: ShoppingCart },
      { id: 'reports', icon: TrendingUp },
      { id: 'billing', icon: Receipt },
    ],
  },
];

// Detail features for each product (with extra icons available)
export const PRODUCT_DETAIL_FEATURES = {
  medicare: [
    { id: 'appointments', icon: CalendarCheck },
    { id: 'records', icon: FileText },
    { id: 'prescriptions', icon: PillBottle },
    { id: 'monitoring', icon: Activity },
    { id: 'multiUser', icon: Users },
    { id: 'security', icon: Shield },
  ],
  tienda360: [
    { id: 'inventory', icon: Boxes },
    { id: 'pos', icon: ShoppingCart },
    { id: 'reports', icon: TrendingUp },
    { id: 'billing', icon: Receipt },
    { id: 'multiBranch', icon: Package },
    { id: 'payments', icon: CreditCard },
  ],
};

// Tech Stack data (brand names — not translated)
export const TECH_STACK_DATA = [
  { id: 'aws', name: 'AWS' },
  { id: 'google-cloud', name: 'Google Cloud' },
  { id: 'microsoft', name: 'Microsoft' },
  { id: 'react', name: 'React' },
  { id: 'nodejs', name: 'Node.js' },
  { id: 'salesforce', name: 'Salesforce' },
  { id: 'docker', name: 'Docker' },
  { id: 'mongodb', name: 'MongoDB' },
];

// Social media links (not translated)
export const SOCIAL_LINKS = [
  { id: 'facebook', icon: 'Facebook', href: '#' },
  { id: 'instagram', icon: 'Instagram', href: '#' },
  { id: 'linkedin', icon: 'Linkedin', href: '#' },
  { id: 'youtube', icon: 'Youtube', href: '#' },
];

// Footer link structure (hrefs only — labels come from translations)
// `type: 'route'` → react-router Link; otherwise anchor to home section.
export const FOOTER_LINKS_CONFIG = [
  {
    key: 'empresa',
    links: [
      { key: 'inicio', href: '/', type: 'route' },
      { key: 'servicios', href: '/#services', type: 'route' },
      { key: 'casosDeExito', href: '/#success-stories', type: 'route' },
      { key: 'productos', href: '/productos', type: 'route' },
    ],
  },
  {
    key: 'productos',
    links: [
      { key: 'medicare', href: '/productos/medicare', type: 'route' },
      { key: 'tienda360', href: '/productos/tienda360', type: 'route' },
      { key: 'allProducts', href: '/productos', type: 'route' },
    ],
  },
  {
    key: 'legal',
    links: [
      { key: 'privacidad', href: '#' },
      { key: 'avisoLegal', href: '#' },
      { key: 'sitemap', href: '#' },
    ],
  },
];

// Company information (non-translatable parts)
export const COMPANY_INFO = {
  name: 'DSW Solutions',
  email: 'contacto@dswsolutions.com',
};

// Form field configurations (labels come from translations)
export const FORM_FIELDS_CONFIG = [
  { id: 'firstName', type: 'text', required: true, icon: 'User' },
  { id: 'lastName', type: 'text', required: true, icon: 'User' },
  { id: 'email', type: 'email', required: true, icon: 'Mail' },
  { id: 'message', type: 'textarea', required: true, icon: 'MessageSquare' },
];
